// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Labels pulled off the thing they label, with a line back to it.
 *
 * A plan gets crowded exactly where it matters: the gate, the corner of the
 * lot, the pinch between the building and the property line. Labels sit on top
 * of each other there, and the only honest fixes are to move the label or to
 * drop it. Moving it is better, as long as it is still obvious what it belongs
 * to — which is what the leader is for.
 *
 * The offset is stored on the element, not the label's absolute position, so a
 * label that has been nudged clear stays clear when the fence it belongs to is
 * dragged somewhere else.
 */

export interface Point { x: number; y: number }
export interface Offset { x: number; y: number }

/** Below this a nudge is a wobble, not an intention, and no leader is drawn. */
export const LEADER_MIN = 6;

/** Whether a label has been moved far enough to need a line back. */
export function hasLeader(offset: Offset | null | undefined, distPx: number): boolean {
  if (!offset) return false;
  if (!Number.isFinite(offset.x) || !Number.isFinite(offset.y)) return false;
  return distPx >= LEADER_MIN;
}

/**
 * Where the leader meets the label.
 *
 * At the label's edge rather than its centre, so the line stops at the box
 * instead of running under the text. The box is treated as a rectangle and
 * the crossing is found on whichever pair of sides the anchor lies beyond,
 * which is exact for a rectangle and cheaper than clipping.
 */
export function edgePoint(
  anchor: Point, centre: Point, halfW: number, halfH: number,
): Point {
  const dx = anchor.x - centre.x;
  const dy = anchor.y - centre.y;
  if (dx === 0 && dy === 0) return { ...centre };

  const w = Math.max(0, halfW);
  const h = Math.max(0, halfH);
  // How far along the ray each pair of sides is crossed; the nearer one wins.
  const tx = dx === 0 ? Infinity : w / Math.abs(dx);
  const ty = dy === 0 ? Infinity : h / Math.abs(dy);
  const t = Math.min(tx, ty);
  return { x: centre.x + dx * t, y: centre.y + dy * t };
}

/**
 * The leader to draw, or null when the label is close enough to speak for
 * itself. Both ends are trimmed: the label end to its edge, the anchor end to
 * a small gap so the line does not touch the thing it points at.
 */
export function leaderLine(
  anchor: Point, centre: Point, halfW: number, halfH: number, gap = 2,
): { from: Point; to: Point } | null {
  const dist = Math.hypot(anchor.x - centre.x, anchor.y - centre.y);
  if (!hasLeader({ x: 1, y: 1 }, dist)) return null;

  // An anchor inside the label box needs no leader, and would get a backwards
  // one: the edge it crosses lies further out than the anchor itself.
  if (Math.abs(anchor.x - centre.x) <= halfW && Math.abs(anchor.y - centre.y) <= halfH) return null;

  const to = edgePoint(anchor, centre, halfW, halfH);
  const len = Math.hypot(anchor.x - to.x, anchor.y - to.y);
  if (len <= gap) return null;

  const k = (len - gap) / len;
  return {
    from: { x: to.x + (anchor.x - to.x) * k, y: to.y + (anchor.y - to.y) * k },
    to,
  };
}

/** An offset cleaned up, so a half-typed or corrupt value cannot move a label
 *  somewhere nobody can reach it. */
export function clampOffset(o: Partial<Offset> | null | undefined, limit = 100): Offset {
  const one = (v: unknown) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(-limit, Math.min(limit, n));
  };
  return { x: one(o?.x), y: one(o?.y) };
}

/** Whether an element's label has been moved at all. */
export function isOffset(o: Offset | null | undefined): boolean {
  return !!o && (Math.abs(o.x) > 0.001 || Math.abs(o.y) > 0.001);
}

/**
 * An offset measured on screen, expressed in the frame of a turned element.
 *
 * An item's name is drawn inside the group that turns with the item, which is
 * how a long object is labelled on a real sheet. That group turns everything
 * in it, the label's offset included, so a label dragged down-right on a
 * trailer lying along the property line would set off down-left instead.
 * Turning the offset back by the same angle means the label goes where it was
 * dragged, whatever the item is doing.
 */
export function unrotateOffset(d: Point, deg: number): Point {
  const r = (-(deg || 0) * Math.PI) / 180;
  if (!Number.isFinite(r) || r === 0) return { x: d.x, y: d.y };
  const c = Math.cos(r), s = Math.sin(r);
  return { x: d.x * c - d.y * s, y: d.x * s + d.y * c };
}
